import graphqlFetch from '@helpers/api/graphql-fetch';
import parseAlgoliaFilters from '@helpers/datocms/algoliaFilters';
import { parseFacetName } from '@helpers/filters/filters';
import { generateUrlWithQueryParameter, REMOVE_FALSY } from '@helpers/url';
import { gql } from 'graphql-request';
import { algoliaAllFiltersGql } from '../taxonomy/gql';

const VALID_STATUS = [200];

export const fixedFiltersGql = gql`
    fixedFilter(filter: {channel: {eq: $channel}, active: {eq: "true"}}) {
      id
      preFiltersConfig {
        ${algoliaAllFiltersGql}
      }
      posFiltersConfig {
        ${algoliaAllFiltersGql}
      }
    }
`;

export const defaultFiltersGql = gql`
    defaultFilter {
      id
      filtersConfig {
        ${algoliaAllFiltersGql}
      }
    }
`;

export const fixedAndDefaultFiltersGqlData = gql`
  query fixedfilter($channel: String){
    ${fixedFiltersGql}
    ${defaultFiltersGql}
  }
`;

export type TFixedAndDefaultFilters = Record<
  'fixedFilter' | 'defaultFilter',
  {
    id: string;
    filtersConfig?: Record<string, any>[];
    preFiltersConfig?: Record<string, any>[];
    posFiltersConfig?: Record<string, any>[];
  }
>;

function setFilterType(filtersConfig, type = 'fixed') {
  return filtersConfig.map(filter => ({
    ...filter,
    type,
  }));
}

function parseFixedFilters(fixedFilter) {
  const { id = null, preFiltersConfig = [], posFiltersConfig = [] } = fixedFilter || {};

  return {
    id,
    preFiltersConfig: setFilterType(preFiltersConfig),
    posFiltersConfig: setFilterType(posFiltersConfig),
  };
}

export async function getFixedAndDefaultFiltersData({
  preview,
  channel = 'site',
}: {
  preview?: boolean;
  channel?: string;
} = {}): Promise<TFixedAndDefaultFilters> {
  const { fixedFilter, defaultFilter } = await graphqlFetch(fixedAndDefaultFiltersGqlData, {
    preview,
    variables: { channel },
  });

  return {
    fixedFilter: parseFixedFilters(fixedFilter),
    defaultFilter,
  };
}

export async function getServerlessFixedAndDefaultFiltersData({
  preview = false,
  channel = 'site',
}: {
  preview: boolean;
  channel?: string;
}): Promise<any> {
  try {
    const url = generateUrlWithQueryParameter(
      `${process.env.NEXT_PUBLIC_HTTP_DOMAIN}/api/datocms/search/fixed-default-filters/${channel}`,
      {
        preview,
      },
      REMOVE_FALSY
    );
    const response = await fetch(url);
    if (!VALID_STATUS.includes(response.status)) {
      throw new Error(`Invalid response status ${response.status}`);
    }

    return await response.json();
  } catch (e) {
    return getFixedAndDefaultFiltersData({
      preview,
      channel,
    });
  }
}

const WIDGET_SECTION_LABELS: Record<string, string> = {
  HierarchicalMenu: 'Categoria',
  RatingMenu: 'Avaliações',
};

export const formatFiltersFixedMobile = (filtersFixedMobileData: {
  preFiltersConfig?: Record<string, any>[];
  posFiltersConfig?: Record<string, any>[];
}) => {
  if (!filtersFixedMobileData) {
    return filtersFixedMobileData;
  }

  const { preFiltersConfig, posFiltersConfig } = filtersFixedMobileData;

  const filtersFixedMobile = parseAlgoliaFilters([
    ...(preFiltersConfig || []),
    ...(posFiltersConfig || []),
  ]);

  return filtersFixedMobile
    .filter(facet => facet.widgetName !== 'RemoveFacets')
    .map(facet => {
      if (facet.widgetName in WIDGET_SECTION_LABELS) {
        const sectionLabel = WIDGET_SECTION_LABELS[facet.widgetName];
        return { ...facet, sectionLabel };
      }

      if (facet.attribute) {
        const label = parseFacetName(facet.attribute);
        return { ...facet, sectionLabel: label.trim() };
      }

      return facet;
    });
};
